import { matchRoutes } from "react-router-dom";

/**
 * Traduz um pathname em identidade ESTÁVEL e SEM DADO PESSOAL para o log de atividade.
 *
 * O problema que isto resolve: gravar `location.pathname` cru colocaria GUID de identidade nas
 * linhas de log. `/users/:id/edit` e `/users/:id/link-plant` identificam pessoa, e `:fmeaId`,
 * `:failureId`, `:groupId`, `:lineId` e `:id` aparecem em cerca de 30 rotas. A heurística que o
 * AppShell usa para breadcrumb (`segment.length > 20 || !isNaN(Number(segment))`) tem falso
 * positivo em slug longo e falso negativo em id curto alfanumérico — não serve como garantia.
 *
 * A solução é casar o pathname contra os PATTERNS declarados e gravar o pattern, nunca o
 * caminho resolvido. Assim o log diz "editou um usuário" sem dizer qual.
 *
 * FALLBACK SEGURO POR CONSTRUÇÃO: quando nenhum pattern casa, devolve `UNMATCHED` — e nunca o
 * pathname. Se alguém acrescentar uma rota em `config/Routes.tsx` e esquecer de acrescentá-la
 * aqui, o efeito é PERDER o evento, não vazar identificador. É a assimetria correta: um evento
 * a menos é irrelevante, um GUID de pessoa numa tabela de log não.
 */

/** Devolvido quando o pathname não casa com nenhum pattern conhecido. */
export const UNMATCHED_ROUTE = "(unmatched)";

/** Devolvido quando não há chave de tela conhecida para o pattern. */
export const UNKNOWN_SCREEN = "unknown";

/**
 * Patterns declarados em `config/Routes.tsx`.
 *
 * MANTENHA EM SINCRONIA ao acrescentar rota lá. A lista existe porque `Routes.tsx` monta as
 * rotas como JSX (`<Route>`), e `matchRoutes` precisa de objetos — não há como derivar sem
 * refatorar o roteador, o que está fora do escopo deste épico.
 */
export const ROUTE_PATTERNS: readonly string[] = [
  "/",
  "/login",
  "/register",
  "/home",
  "/home-dashboard",
  "/dashboard",
  "/config",
  // --- POC Localiza ---
  "/occupancy-map",
  "/monitoring/dashboard",
  "/monitoring/timeline",
  "/reports/synoptic",
  "/reports/gross-volume-produced",
  "/reports/oee-performance",
  "/reports/state-pareto",
  "/reports/hourly-production",
  "/reports/preparation-time",
  "/reports/accumulated-production",
  // --- Nex ---
  "/report",
  "/reports/failures-by-operation",
  "/failure-analysis",
  "/fast-response",
  "/fast-response-email",
  "/error-proofs",
  "/devices",
  "/devices/register-param",
  "/stations/register",
  "/companies/create",
  "/users",
  "/users/create",
  "/users/:id/edit",
  "/users/:id/link-plant",
  "/groups",
  "/groups/:groupId/edit",
  "/groups/:groupId/permissions",
  "/families",
  "/families/create",
  "/families/:id",
  "/families/:id/edit",
  "/family-fmeas",
  "/family-fmeas/create",
  "/family-fmeas/:id",
  "/family-fmeas/:id/edit",
  "/family-fmeas/:fmeaId/failures",
  "/family-fmeas/:fmeaId/failures/create",
  "/family-fmeas/:fmeaId/failures/:id/edit",
  "/family-fmeas/:fmeaId/failures/:failureId/:tab",
  "/products",
  "/products/create",
  "/products/:id",
  "/products/:id/edit",
  "/plants",
  "/plants/create",
  "/plants/:id",
  "/plants/:id/edit",
  "/production-units",
  "/production-units/create",
  "/production-units/:id",
  "/production-units/:id/edit",
  "/areas",
  "/areas/create",
  "/areas/:id",
  "/areas/:id/edit",
  "/lines",
  "/lines/create",
  "/lines/:id",
  "/lines/:id/edit",
  "/lines/assign-families/:lineId",
  "/sublines",
  "/sublines/create",
  "/sublines/:id",
  "/sublines/:id/edit",
  "/operations",
  "/operations/create",
  "/operations/:id",
  "/operations/:id/edit",
  "/fmeas",
  "/fmeas/create",
  "/fmeas/:fmeaId",
  "/fmeas/:fmeaId/edit",
  "/fmeas/:fmeaId/select-inherited-failures",
  "/fmeas/:fmeaId/steps/1",
  "/fmeas/:fmeaId/steps/2",
  "/fmeas/:fmeaId/steps/3",
  "/fmeas/:fmeaId/steps/4",
  "/fmeas/:fmeaId/steps/:stepNumber",
  "/fmeas/:fmeaId/failures",
  "/fmeas/:fmeaId/failures/create",
  "/fmeas/:fmeaId/failures/:id/edit",
  "/fmeas/:fmeaId/failures/:failureId/:tab",
  "/fmeas/:fmeaId/failures/:failureId/action-plans",
  "/fmeas/:fmeaId/failures/:failureId/action-plans/create",
  "/fmeas/:fmeaId/failures/:failureId/action-plans/:id/edit",
];

/**
 * Chave estável de tela por pattern.
 *
 * NÃO usa o rótulo traduzido do i18n de propósito: o texto muda com o idioma do usuário (o app
 * tem pt e en) e os namespaces são carregados por HTTP em runtime, então um evento emitido
 * antes do namespace chegar gravaria a própria chave crua (`menu:grossVolumeProduced`). O
 * relatório de log tem de ser comparável entre um usuário pt e um en.
 *
 * Só os patterns com nome próprio entram aqui. O resto cai no pattern como chave, o que já é
 * estável e sem dado pessoal.
 */
const SCREEN_KEYS: Readonly<Record<string, string>> = {
  "/occupancy-map": "monitoring.occupancy-map",
  "/monitoring/dashboard": "monitoring.dashboard",
  "/monitoring/timeline": "monitoring.timeline",
  "/reports/synoptic": "monitoring.synoptic",
  "/reports/gross-volume-produced": "report.gross-volume-produced",
  "/reports/oee-performance": "report.oee-performance",
  "/reports/state-pareto": "report.state-pareto",
  "/reports/hourly-production": "report.hourly-production",
  "/reports/preparation-time": "report.preparation-time",
  "/reports/accumulated-production": "report.accumulated-production",
};

/** Objetos de rota para o `matchRoutes` — montados uma vez. */
const ROUTE_OBJECTS = ROUTE_PATTERNS.map((path) => ({ path }));

export interface ScreenIdentity {
  /** Chave estável da tela. */
  screenKey: string;
  /** Pattern da rota, já sem valores de parâmetro. */
  routePattern: string;
}

/**
 * Identidade da tela para um pathname.
 *
 * ```
 * resolveScreenIdentity("/reports/state-pareto")
 * // { screenKey: "report.state-pareto", routePattern: "/reports/state-pareto" }
 *
 * resolveScreenIdentity("/users/8f3c.../edit")
 * // { screenKey: "/users/:id/edit", routePattern: "/users/:id/edit" }  ← id não aparece
 *
 * resolveScreenIdentity("/rota-que-ninguem-declarou")
 * // { screenKey: "unknown", routePattern: "(unmatched)" }
 * ```
 */
export const resolveScreenIdentity = (pathname: string): ScreenIdentity => {
  const matches = matchRoutes(ROUTE_OBJECTS, pathname);

  // matchRoutes devolve a cadeia do menos ao mais específico; o último é a folha.
  const pattern = matches?.[matches.length - 1]?.route?.path;

  if (!pattern) {
    return { screenKey: UNKNOWN_SCREEN, routePattern: UNMATCHED_ROUTE };
  }

  return { screenKey: SCREEN_KEYS[pattern] ?? pattern, routePattern: pattern };
};
